import type { PropsWithChildren } from 'react'
import React from 'react'
import type { FormikValues } from 'formik'
import { useField } from 'formik'

import { TtSelect } from '@23/react'

import type {
  FieldNameMatchingAcceptedType,
  FormikFormControlProps,
} from './index'

const TtSelectFormik = <FV extends FormikValues>({
  name,
  multiple,
  validate,
  invalid,
  errorText,
  search,
  children,
  ...props
}: PropsWithChildren<FormikFormControlProps<'tt-select'>> &
  (
    | {
        multiple?: false
        name: FieldNameMatchingAcceptedType<
          FV,
          HTMLTtSelectElement['value'] | undefined
        >
      }
    | {
        multiple: true
        name: FieldNameMatchingAcceptedType<
          FV,
          HTMLTtSelectElement['value'][] | undefined
        >
      }
  )) => {
  type Value =
    Required<typeof multiple> extends true
      ? HTMLTtSelectElement['value'][]
      : HTMLTtSelectElement['value']

  const [field, meta, helpers] = useField<Value>({
    name,
    validate,
    multiple,
  })

  const currentValues = field.value as unknown as HTMLTtSelectElement['value'][]

  return (
    <TtSelect
      {...props}
      name={field.name}
      multiple={multiple}
      search={search || 'disabled'}
      value={
        multiple
          ? search === 'external'
            ? currentValues.join(', ')
            : undefined
          : field.value
      }
      onTt-change={(event) => {
        if (search !== 'external') {
          helpers.setValue(event.detail.value as Value)
        }
      }}
      onTt-diff={(event) => {
        if (search === 'external') {
          if (!multiple) {
            helpers.setValue(event.detail.added[0])
          } else {
            const updatedValues = [
              ...currentValues.filter((v) => !event.detail.removed.includes(v)),
              ...event.detail.added,
            ]
            helpers.setValue(updatedValues as unknown as Value)
          }
        }
      }}
      invalid={meta.touched && (!!meta.error || invalid)}
      errorText={meta.error ?? errorText}
    >
      {children}
    </TtSelect>
  )
}

export { TtSelectFormik }
